import cloneDeep from 'lodash/cloneDeep';
import set from 'lodash/set';

import { showErrorToast } from '@/plugins/vueQuery';

// example
// a: {
//  b: {
//    c: 1,
//    d: 2
//  },
//  e: 3
// };
// Output: ['a.b.c', 'a.b.d', 'a.e']
function getAllNestedKeys(obj, parentKey = '') {
  let keys = [];

  for (const key in obj) {
    const fullKey = parentKey ? `${parentKey}.${key}` : key;
    keys.push(fullKey);

    if (typeof obj[key] === 'object' && obj[key] !== null && !Array.isArray(obj[key])) {
      keys = keys.concat(getAllNestedKeys(obj[key], fullKey));
    }
  }

  return keys;
}

function collectionValidation(errors) {
  // [0].name = message
  if (errors.length === 0) {
    return [];
  }
  errors.forEach((error) => {
    Object.keys(error).forEach((errorKey) => {
      if (error[errorKey] && error[errorKey].length > 0) {
        error[errorKey] = changeValidationMessage(error[errorKey][0], errorKey);
      }
    });
  });

  return errors;
}

function changeValidationMessage(errItem, key) {
  let message;
  switch (errItem?.$validator) {
    case 'numeric':
      message = 'Only numbers are allowed';
      break;
    case 'integer':
      message = 'Only whole numbers are allowed.';
      break;
    case 'required':
      message = 'required';
      break;
    case 'between':
      message = `must be between ${errItem.$params.min} and ${errItem.$params.max}`;
      break;
    case 'minValue':
      message = `must be greater than or equal to ${errItem.$params.min}`;
      break;
    default:
      if (errItem) {
        message = errItem.$message.replace('value', key);
      }
  }

  return message;
}

function getFirstErrorMessage(errors) {
  if (!errors) {
    return null;
  }

  if (typeof errors === 'string' && errors.length > 0) {
    return errors;
  }

  if (Array.isArray(errors)) {
    for (const error of errors) {
      const message = getFirstErrorMessage(error);
      if (message) {
        return message;
      }
    }

    return null;
  }

  if (typeof errors === 'object') {
    for (const value of Object.values(errors)) {
      const message = getFirstErrorMessage(value);
      if (message) {
        return message;
      }
    }
  }

  return null;
}

function getFormErrorMessage(errors, preferredFields = []) {
  if (!errors || typeof errors !== 'object') {
    return null;
  }

  for (const field of preferredFields) {
    const message = getFirstErrorMessage(errors[field]);
    if (message) {
      return message;
    }
  }

  return getFirstErrorMessage(errors);
}

function getFirstErrorField(errors, parentKey = '') {
  if (!errors || typeof errors !== 'object') {
    return null;
  }

  for (const [key, value] of Object.entries(errors)) {
    const fullKey = parentKey ? `${parentKey}.${key}` : key;

    if (typeof value === 'string' && value.length > 0) {
      return fullKey;
    }

    if (Array.isArray(value)) {
      continue;
    }

    if (value && typeof value === 'object') {
      const nestedKey = getFirstErrorField(value, fullKey);
      if (nestedKey) {
        return nestedKey;
      }
    }
  }

  return null;
}

function focusFirstFormError(errors) {
  if (typeof document === 'undefined') {
    return;
  }

  const field = getFirstErrorField(errors);
  if (!field) {
    return;
  }

  const selectors = [`[name="${field}"]`, `#${field}`];

  for (const selector of selectors) {
    const element = document.querySelector(selector);

    if (!element) {
      continue;
    }

    element.scrollIntoView?.({
      behavior: 'smooth',
      block: 'center',
    });

    const focusElement = () => {
      element.focus?.();
    };

    if (typeof requestAnimationFrame === 'function') {
      requestAnimationFrame(focusElement);
    } else {
      setTimeout(focusElement, 0);
    }

    return;
  }
}

function registerErrorMessages(app) {
  function $processMessage(ctx, message, simpleMessage) {
    const messageId = message.type ? message.type.split('urn:uuid:')[1] : false;

    let mess = '';
    if (!message.type) {
      mess = simpleMessage ? message.title : ctx.$t('errorMessages.unknownError');
    } else {
      mess = ctx.$t(`errorMessages.${messageId}`);
    }

    if (mess && mess.includes('errorMessages.')) {
      return simpleMessage ? message.title : ctx.$t('errorMessages.unknownError');
    } else if (!message.parameters || Object.keys(message.parameters).length === 0) {
      return mess;
    } else {
      let processedMess = mess;
      Object.keys(message.parameters).forEach((key) => {
        let type = message.parameters[key.toLowerCase()];
        if (message.parameters[key.toLowerCase()] === 'comment' || message.parameters[key.toLowerCase()] === 'post') {
          type = `${message.parameters[key.toLowerCase()]}One`;
        }
        processedMess = processedMess.replace(`{{ ${key.toLowerCase()} }}`, ctx.$t(type));
      });

      return processedMess;
    }
  }

  app.config.globalProperties.$parseErrors = function (errorsArr, errorDetail) {
    const errors = {};

    errorsArr.forEach((el) => {
      set(errors, el.propertyPath, errorDetail ? el.detail : $processMessage(this, el));
    });

    return errors;
  };

  app.config.globalProperties.$arrayErrorCheck = function (error, index, key) {
    if (!error) {
      return null;
    }
    if (!error[index]) {
      return null;
    }

    return error[index][key];
  };

  app.config.globalProperties.$parseValidationErrors = function (form, validation) {
    const errors = validation.$errors;
    const errs = {};

    getAllNestedKeys(form).forEach((key) => {
      const errItem = errors.find((error) => error.$propertyPath == `${validation.$path}.${key}`);

      if (!errItem) {
        return;
      }

      if (errItem?.$validator === '$each') {
        errs[key] = collectionValidation(cloneDeep(errItem.$response.$errors));
      } else {
        errs[key] = changeValidationMessage(errItem, key);
      }
    });

    return errs;
  };

  app.config.globalProperties.$parseAuthErrors = function (error) {
    const errors = {};

    if (error.errors) {
      error.errors.forEach((el) => {
        errors[el.field] = el.message;
      });
    } else {
      errors[error.field] = error.message;
    }

    return errors;
  };

  app.config.globalProperties.$getFirstErrorMessage = getFirstErrorMessage;
  app.config.globalProperties.$getFormErrorMessage = getFormErrorMessage;
  app.config.globalProperties.$focusFirstFormError = focusFirstFormError;
  app.config.globalProperties.$extractFormErrors = function (error, options = {}) {
    const { errorDetail = false } = options;
    const responseData = error?.response?.data || error || {};

    if (!Array.isArray(responseData.errors)) {
      return {};
    }

    return this.$parseErrors(responseData.errors, errorDetail) || {};
  };

  app.config.globalProperties.$handleFormErrors = function (error, options = {}) {
    const {
      errorDetail = false,
      showToastForValidationErrors = false,
      showGlobalError = true,
      focusFirstError = false,
    } = options;
    const responseData = error?.response?.data || error || {};
    const errors = this.$extractFormErrors(error, { errorDetail });

    if (Object.keys(errors).length > 0) {
      if (focusFirstError) {
        focusFirstFormError(errors);
      }

      if (showToastForValidationErrors) {
        this.$alertError(responseData.detail || getFormErrorMessage(errors));
      }

      return errors;
    }

    if (showGlobalError) {
      this.$alertError(responseData.detail || responseData.title);
    }

    return {};
  };

  app.config.globalProperties.$parseMessage = function (message) {
    const customTextId = '1a4feb44-04b7-445c-97ae-b1341910fdfe';
    const messageId = message.type ? message.type.split('urn:uuid:')[1] : false;

    return messageId === customTextId ? message.title : $processMessage(this, message, true);
  };

  app.config.globalProperties.$alertError = function (error) {
    if (error === 'console-alert') {
      return;
    }
    showErrorToast(error || 'Something went wrong');
  };
}

export default registerErrorMessages;
